import { ref, onUnmounted } from 'vue'

export function useSpeech(getContent: () => string, getLanguage: () => string) {
  const isReading = ref(false)
  const speechUtterance = ref<SpeechSynthesisUtterance | null>(null)

  const setupSpeechHandlers = (utterance: SpeechSynthesisUtterance) => {
    utterance.onstart = () => { isReading.value = true }
    utterance.onend = () => { isReading.value = false; speechUtterance.value = null }
    utterance.onerror = (event) => {
      isReading.value = false
      speechUtterance.value = null
      console.error('Speech synthesis error:', event)
    }
  }

  const scoreVoice = (voice: SpeechSynthesisVoice, languageCode: string): number => {
    let score = 0
    const name = voice.name.toLowerCase()
    if (name.includes('enhanced') || name.includes('premium') || name.includes('neural')) score += 100
    if (name.includes('siri') || name.includes('alex') || name.includes('samantha')) score += 90
    if (name.includes('natural') || name.includes('quality')) score += 80
    if (languageCode === 'en') {
      if (name.includes('alex')) score += 50
      if (name.includes('samantha')) score += 45
      if (name.includes('daniel')) score += 40
      if (name.includes('karen')) score += 35
      if (name.includes('microsoft zira')) score += 30
      if (name.includes('microsoft david')) score += 25
    }
    if (voice.localService) score += 20
    if (voice.default) score += 10
    if (name.includes('female') || name.includes('woman') ||
        name.includes('samantha') || name.includes('karen') ||
        name.includes('zira') || name.includes('hazel')) score += 5
    return score
  }

  const findBestVoice = (voices: SpeechSynthesisVoice[], languageCode: string): SpeechSynthesisVoice | null => {
    const languageMapping: Record<string, string[]> = {
      'en': ['en-us', 'en-gb', 'en-au', 'en-ca', 'en'],
      'zh': ['zh-cn', 'zh-hans-cn', 'zh'],
      'zh-tw': ['zh-tw', 'zh-hant-tw', 'zh-hant'],
      'zh-hk': ['zh-hk', 'zh-hant-hk', 'zh-hant'],
      'hi': ['hi-in', 'hi'], 'es': ['es-es', 'es-mx', 'es-us', 'es'],
      'ar': ['ar-sa', 'ar-eg', 'ar'], 'fr': ['fr-fr', 'fr-ca', 'fr'],
      'bn': ['bn-bd', 'bn-in', 'bn'], 'pt': ['pt-br', 'pt-pt', 'pt'],
      'ru': ['ru-ru', 'ru'], 'ja': ['ja-jp', 'ja'], 'ko': ['ko-kr', 'ko'],
      'de': ['de-de', 'de'], 'it': ['it-it', 'it'], 'nl': ['nl-nl', 'nl'],
      'tr': ['tr-tr', 'tr'], 'th': ['th-th', 'th'], 'vi': ['vi-vn', 'vi'],
      'fa': ['fa-ir', 'fa'], 'he': ['he-il', 'he']
    }

    const priorityLangCodes = languageMapping[languageCode] || [languageCode, 'en']
    let candidateVoices: { voice: SpeechSynthesisVoice; score: number }[] = []

    for (const langCode of priorityLangCodes) {
      const matching = voices.filter(v =>
        v.lang.toLowerCase().startsWith(langCode.toLowerCase()) ||
        v.lang.toLowerCase().replace('-', '_').startsWith(langCode.toLowerCase().replace('-', '_'))
      )
      for (const voice of matching) {
        candidateVoices.push({ voice, score: scoreVoice(voice, languageCode) })
      }
      if (candidateVoices.length > 0) break
    }

    if (candidateVoices.length > 0) {
      candidateVoices.sort((a, b) => b.score - a.score)
      return candidateVoices[0].voice
    }
    return null
  }

  const performSpeech = () => {
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      /WebKit/.test(navigator.userAgent) && !/CriOS/.test(navigator.userAgent)
    const languageCode = getLanguage().toLowerCase()
    const contentToRead = getContent()

    if (!contentToRead) {
      alert('No content available to read. Please wait for the content to load.')
      return
    }

    if (isReading.value) {
      window.speechSynthesis.cancel()
      isReading.value = false
      speechUtterance.value = null
      return
    }

    // Preprocess for non-Western languages
    const nonWesternLanguages = ['zh', 'zh-tw', 'zh-hk', 'ar', 'hi', 'ja', 'ko', 'th', 'ur', 'bn', 'fa', 'he']
    let processedContent = contentToRead

    if (nonWesternLanguages.includes(languageCode)) {
      processedContent = contentToRead
        .replace(/^\d+(\.\d+)?$/, '')
        .replace(/^\s*\d+\s*$/, '')
        .replace(/^[0-9\.\,\s]+$/, '')
        .replace(/^[a-zA-Z0-9\s\.\,\-]+$/, '')
        .trim() || contentToRead

      const languageMarkers: Record<string, string> = {
        'zh': '　', 'zh-tw': '　', 'zh-hk': '　', 'ar': '؛', 'hi': '।',
        'ja': '　', 'ko': '、', 'th': '฿', 'ur': '؛', 'bn': '।', 'fa': '؛', 'he': '־'
      }
      if (processedContent.length < 50 && languageMarkers[languageCode]) {
        processedContent = languageMarkers[languageCode] + processedContent
      }
    }

    const utterance = new SpeechSynthesisUtterance(processedContent)
    speechUtterance.value = utterance
    utterance.rate = isIOSSafari ? 0.8 : 0.9
    utterance.pitch = 1.0
    utterance.volume = 1.0

    if (isIOSSafari) {
      const iOSLangMap: Record<string, string> = { 'zh': 'zh-CN', 'zh-tw': 'zh-TW', 'zh-hk': 'zh-HK' }
      utterance.lang = iOSLangMap[languageCode] || languageCode

      const loadVoicesWithRetry = (): Promise<SpeechSynthesisVoice[]> => new Promise(resolve => {
        const voices = window.speechSynthesis.getVoices()
        if (voices.length > 0) { resolve(voices); return }
        const handler = () => {
          window.speechSynthesis.removeEventListener('voiceschanged', handler)
          resolve(window.speechSynthesis.getVoices())
        }
        window.speechSynthesis.addEventListener('voiceschanged', handler)
        setTimeout(() => resolve(window.speechSynthesis.getVoices()), 500)
      })

      loadVoicesWithRetry().then(voices => {
        const voice = findBestVoice(voices, languageCode)
        if (voice) utterance.voice = voice
        setupSpeechHandlers(utterance)
        window.speechSynthesis.speak(utterance)
      })
    } else {
      utterance.lang = languageCode
      const voices = window.speechSynthesis.getVoices()
      const voice = findBestVoice(voices, languageCode)
      if (voice) utterance.voice = voice
      setupSpeechHandlers(utterance)
      window.speechSynthesis.speak(utterance)
    }
  }

  const readContent = () => {
    if (!window.speechSynthesis) {
      console.error('Speech synthesis not supported in this browser')
      return
    }
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      /WebKit/.test(navigator.userAgent) && !/CriOS/.test(navigator.userAgent)

    if (isIOSSafari) {
      window.speechSynthesis.getVoices()
      setTimeout(() => performSpeech(), 100)
    } else {
      performSpeech()
    }
  }

  onUnmounted(() => {
    if (isReading.value && window.speechSynthesis) {
      window.speechSynthesis.cancel()
      isReading.value = false
      speechUtterance.value = null
    }
  })

  return { isReading, readContent }
}
